import { useEffect, useMemo, useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { Country } from '../../src/lib/location-api';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

interface PhoneInputProps {
  countries: Country[];
  value?: string;
  onChange: (value: string) => void;
  defaultCountryIso?: string;
  onManualInteraction?: () => void;
  disabled?: boolean;
}

const PRIORITY_ISOS = ['MX', 'US', 'CA'];

function getDigits(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

export function PhoneInput({
  countries,
  value = '',
  onChange,
  defaultCountryIso,
  onManualInteraction,
  disabled,
}: PhoneInputProps) {
  const [selectedCountryIso, setSelectedCountryIso] = useState(defaultCountryIso || '');
  const [nationalNumber, setNationalNumber] = useState('');

  const sortedCountries = useMemo(() => {
    return [...countries].sort((a, b) => {
      const aPriority = PRIORITY_ISOS.indexOf((a.iso2 || '').toUpperCase());
      const bPriority = PRIORITY_ISOS.indexOf((b.iso2 || '').toUpperCase());
      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;
      return a.name.localeCompare(b.name, 'es');
    });
  }, [countries]);

  useEffect(() => {
    if (defaultCountryIso) {
      setSelectedCountryIso(defaultCountryIso);
    }
  }, [defaultCountryIso]);

  useEffect(() => {
    const normalized = String(value || '').trim();

    if (!normalized) {
      setNationalNumber('');
      return;
    }

    if (!normalized.startsWith('+')) {
      setNationalNumber(getDigits(normalized));
      return;
    }

    const parsed = parsePhoneNumberFromString(normalized);
    if (parsed) {
      setNationalNumber(parsed.nationalNumber || '');
      if (parsed.country) {
        setSelectedCountryIso(parsed.country);
      }
      return;
    }

    // Fallback para E.164 parcial: nunca mostrar el prefijo en el input nacional.
    const selectedCountry = countries.find((country) => country.iso2 === selectedCountryIso);
    const selectedPhonecode = getDigits(String(selectedCountry?.phonecode || ''));
    const fullDigits = getDigits(normalized);
    if (selectedPhonecode && fullDigits.startsWith(selectedPhonecode)) {
      setNationalNumber(fullDigits.slice(selectedPhonecode.length));
      return;
    }

    // Ultimo fallback: intentar detectar prefijo por metadata de paises.
    const possibleCodes = countries
      .map((country) => getDigits(String(country.phonecode || '')))
      .filter((code) => code.length > 0)
      .sort((a, b) => b.length - a.length);

    const matchedCode = possibleCodes.find((code) => fullDigits.startsWith(code));
    if (matchedCode) {
      setNationalNumber(fullDigits.slice(matchedCode.length));
      return;
    }

    setNationalNumber(fullDigits);
  }, [value, countries, selectedCountryIso]);

  const emitValue = (countryIso: string, national: string) => {
    const digits = getDigits(national);
    if (!digits) {
      onChange('');
      return;
    }

    const country = countries.find((item) => item.iso2 === countryIso);
    const phonecode = getDigits(String(country?.phonecode || ''));
    if (phonecode) {
      onChange(`+${phonecode}${digits}`);
      return;
    }

    // Fallback legacy si no hay prefijo disponible para ese pais.
    onChange(digits);
  };

  const handleCountryChange = (iso: string) => {
    onManualInteraction?.();
    setSelectedCountryIso(iso);
    emitValue(iso, nationalNumber);
  };

  const handleNumberChange = (rawValue: string) => {
    onManualInteraction?.();
    const digits = getDigits(rawValue);
    setNationalNumber(digits);
    emitValue(selectedCountryIso, digits);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,240px)_1fr] gap-2">
      <Select value={selectedCountryIso} onValueChange={handleCountryChange} disabled={disabled}>
        <SelectTrigger aria-label="Código de país del teléfono" className="bg-white">
          <SelectValue placeholder="País de teléfono" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {sortedCountries.map((country) => {
            const dialingCode = String(country.phonecode || '').replace(/\D/g, '');
            const prefix = dialingCode ? `+${dialingCode}` : '-';
            return (
              <SelectItem key={country.iso2} value={country.iso2}>
                {country.name === 'Mexico' ? 'México' : country.name} ({prefix})
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      <Input
        value={nationalNumber}
        onChange={(event) => handleNumberChange(event.target.value)}
        inputMode="numeric"
        autoComplete="tel-national"
        placeholder="Número de teléfono"
        aria-label="Número nacional de teléfono"
        disabled={disabled}
      />
    </div>
  );
}
